import {
	Component,
	Inject,
	OnDestroy,
	OnInit,
} from '@angular/core';
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	Validators,
} from '@angular/forms';
import {
	MAT_DIALOG_DATA,
	MatDialog,
	MatDialogRef,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ApplicationQuery } from '@app/store/application/application.query';
import { ApplicationService } from '@app/store/application/application.service';
import { BrandConfigQuery } from '@app/store/brand/brand.query';
import { BreedsQuery } from '@app/store/breeds/breeds.query';
import { QuoteSessionQuery } from '@app/store/session/session.query';
import { QuoteSessionService } from '@app/store/session/session.service';
import { Ages } from '@constants/ages';
import { EVENTS, FORM_FIELD_TYPES, FORM_NAMES } from '@constants/analytics';
import { filteredBreeds } from '@constants/breeds';
import { NAME_MAX_LENGTH } from '@constants/name-length';
import { ROUTES } from '@constants/routes';
import { petNameRegEx } from '@constants/validation-regex';
import { Breed } from '@models/Breed';
import { Pet } from '@models/Pet';
import { WhitelabelProductFeatures } from '@models/WhitelabelConfig';
import { AnalyticsService } from '@services/analytics.service';
import { LoggingService } from '@services/logging.service';
import { OrchestratorService } from '@services/orchestrator/orchestrator.service';
import { UtmService } from '@services/utm.service';
import { Observable, Subject, Subscription, firstValueFrom } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

@Component({
	selector: 'qe-add-edit-pet',
	templateUrl: './add-edit-pet.component.html',
	styleUrls: ['./add-edit-pet.component.scss'],
})
export class AddEditPetComponent implements OnInit, OnDestroy {
	public showAgeError: boolean = false;
	public ageOptions: Map<number, string> = new Ages().values;
	public addEditPetForm: FormGroup;
	public breedsList$!: Observable<Breed[]>;
	public pet!: Pet;
	public tooManyPets: boolean = false;
	public featureFlags$!: Observable<WhitelabelProductFeatures>;
	public isAgeError: boolean = false;
	public formattedPhoneNumber?: string;
	public serviceDogEnabled: boolean = false;
	public focusedInput?: string = undefined;
	public readonly phoneNumber$: Subscription | undefined;
	private readonly ngUnsubscribe$: Subject<boolean> = new Subject();

	public get name(): AbstractControl | null {
		return this.addEditPetForm.get('name');
	}

	public get species(): AbstractControl | null {
		return this.addEditPetForm.get('species');
	}

	public get gender(): AbstractControl | null {
		return this.addEditPetForm.get('gender');
	}

	public get breed(): AbstractControl | null {
		return this.addEditPetForm.get('breed');
	}

	public get serviceDog(): AbstractControl | null {
		return this.addEditPetForm.get(`serviceDog`);
	}

	public get age(): AbstractControl | null {
		return this.addEditPetForm.get('age');
	}

	constructor(
		public readonly dialogRef: MatDialogRef<AddEditPetComponent>,
		@Inject(MAT_DIALOG_DATA) public data: { mode: 'add' | 'edit' },
		private readonly dialog: MatDialog,
		private readonly fb: FormBuilder,
		private readonly router: Router,
		private readonly brandConfigQuery: BrandConfigQuery,
		private readonly breedsQuery: BreedsQuery,
		private readonly storeActions: QuoteSessionService,
		private readonly orchestratorService: OrchestratorService,
		private readonly sessionQuery: QuoteSessionQuery,
		private readonly appQuery: ApplicationQuery,
		private readonly appActions: ApplicationService,
		private readonly gtmService: AnalyticsService,
		private readonly loggingService: LoggingService,
		private readonly utmService: UtmService,
	) {
		this.featureFlags$ = this.brandConfigQuery.featureFlags$();
		this.addEditPetForm = this.fb.group({
			name: [
				'',
				[
					Validators.required,
					Validators.pattern(petNameRegEx),
					Validators.maxLength(NAME_MAX_LENGTH),
				],
			],
			species: ['dog', Validators.required],
			serviceDog: ['no', { updateOn: `change` }],
			gender: ['male', Validators.required],
			breed: ['', Validators.required],
			age: [undefined, Validators.required],
		});

		this.species?.valueChanges
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe(() => {
				if (!!this.breed?.value) {
					this.breed.patchValue(``);
				}
			});

		const maxNumberOfPets: number = 15;
		if (this.sessionQuery.petsStateValue().length === maxNumberOfPets) {
			this.dialogRef.close();
			import(`@shared/base-error-modal/base-error-modal.component`)
				.then((c) =>
					this.dialog.open(c.BaseErrorModalComponent, {
						data: {
							messages: [
								`The maximum number of pets allowed on a policy is 15.`,
							],
						},
					})
				)
				.catch((err) => this.loggingService.error(err));

			return;
		}
	}

	async ngOnInit(): Promise<void> {
		this.dialogRef
			.afterOpened()
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe(
				() => {
					if (this.data.mode === `edit`) {
						// FIXME: Find way to pass active pet
						const activePet = this.sessionQuery.getPetById(
							this.appQuery.getActivePetId() ?? ``
						);
						if (!!activePet) {
							this.pet = activePet;
							const {
								name,
								species,
								gender,
								breedId: breed,
								ageInYears: age,
								isServiceDog
							} = this.pet;

							this.addEditPetForm.patchValue({
								name,
								species: species?.toLowerCase() || 'dog',
								gender: gender?.toLowerCase() || 'male',
								age,
								serviceDog: isServiceDog ? 'yes' : 'no',
								breed
							});
						}
					} else {
						this.addEditPetForm.patchValue({
							species: 'dog',
							gender: 'male',
							serviceDog: 'no'
						});
					}
				},
				(err) => this.loggingService.error(err)
			);

		this.serviceDogEnabled = (await firstValueFrom(this.featureFlags$)).find((flag) => flag?.feature === 'service_dog')
			?.enabled || false;

		this.species?.valueChanges.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(
			(species) => {
				this.breedsList$ = this.breedsQuery.selectAll({
					// eslint-disable-next-line @typescript-eslint/no-magic-numbers
					filterBy: ({ speciesName, id }) =>
						speciesName.toLowerCase() === species &&
						!filteredBreeds.includes(+id), // eslint-disable-line  @typescript-eslint/no-magic-numbers
				});
			},
			(err) => this.loggingService.error(err)
		);
	}

	public onFocus(el: string): void {
		this.focusedInput = el;
	}

	public onBlur(el: string): void {
		this.focusedInput = undefined;

		this.gtmService.push({
			event: EVENTS.FORM_FIELD,
			form_name: FORM_NAMES.ADD_EDIT_PET,
			field_name: el,
			field_value: this.addEditPetForm.getRawValue().name,
			field_type: FORM_FIELD_TYPES.INPUT,
			field_id: `${FORM_FIELD_TYPES.INPUT}-${FORM_NAMES.ADD_EDIT_PET}-${el}`
		});
	}

	public handleEpiSelectionChange(el: HTMLEpiSelectElement): void {
		this.gtmService.push({
			event: EVENTS.FORM_FIELD,
			form_name: `${this.data.mode}-pet-modal`,
			field_name: `${el.id}-${el.value}`,
			field_value: el.value,
			field_type: FORM_FIELD_TYPES.SELECT,
			field_id: `${FORM_FIELD_TYPES.SELECT}-${el.id}-${el.value}`
		});
	}

	public editPet(): void {
		const {
			name,
			species,
			gender,
			breed,
			serviceDog,
			age: ageInYears,
		} = this.addEditPetForm.getRawValue();
		/** HACK: */
		if (typeof breed === `string`) {
			this.breed?.patchValue(null);
			this.breed?.updateValueAndValidity();

			return;
		}
		if (ageInYears === `999`) {
			this.showAgeError = true;

			return;
		}
		let updatePetRequest: Pet = {
			name,
			species,
			gender,
			breedId: breed,
			breedName:
				this.breedsQuery.getAll().find(({ id }) => id === breed)?.name ?? ``,
			ageInYears,
			petId: this.pet.petId,
			examFeeCoverage: this.pet.examFeeCoverage,
			drugDental: this.pet.drugDental,
			annualDeductibleValue: this.pet.annualDeductibleValue,
			annualReimbursementValue: this.pet.annualReimbursementValue,
			reimbursementValue: this.pet.reimbursementValue,
			wellnessValue: this.pet.wellnessValue,
		};

		if (species === 'dog') {
			updatePetRequest = {
				...updatePetRequest,
				isServiceDog: serviceDog === 'yes' ? true : false,
			};
		}

		this.orchestratorService
			.updatePet(updatePetRequest)
			.pipe(first())
			.subscribe(
				({ quoteDetails, sessionType, id, pets }) => {
					// eslint-disable-next-line @typescript-eslint/no-magic-numbers
					this.appActions.setLoading(
						true,
						`Calculating new price & updating coverage...`,
						2000 // eslint-disable-line  @typescript-eslint/no-magic-numbers
					);
					this.storeActions.update({
						quoteDetails: {
							...quoteDetails,
							hadVetVisit: this.sessionQuery.getVetVisit(),
						},
						sessionType,
						pets,
						id,
					});
					this.appActions.setActivePet(this.pet.petId ?? ``);
					this.dialogRef.close(true);
					this.router.navigate([ROUTES.SELECT_A_POLICY]);
					const loaderTimeout: number = this.appQuery.getLargeLoaderTimeout();
					if (!!loaderTimeout) {
						setTimeout(() => {
							this.appActions.setLoading(false);
						}, loaderTimeout);
					}
				},
				(err) => {
					this.loggingService.error(err);
					if (err.error.detail.includes('CA_SALES_VALIDATION_MESSAGE')) {
						this.isAgeError = true;
					} else {
						this.router.navigate([ROUTES.ERROR]);
					}
				}
			);

		this.dialogRef
			.afterClosed()
			.pipe(first())
			.subscribe(() => {
				const petIndex = this.sessionQuery.getPetIndex(
					this.appQuery.getActivePetId() ?? ``
				);
				this.gtmService.push({
					event: `Edit Pet (Pet ${petIndex})`,
					petIndex,
					queryString: `?editpet=${petIndex}`,
				});
			});
	}

	public handleSubmit(): void {
		this.data.mode === `add` && this.addPet();
		this.data.mode === `edit` && this.editPet();
	}

	public cancel(): void {
		this.dialogRef.close();
	}

	public onRadioChange(formField: string, value: string): void {
		this.gtmService.push({
			event: EVENTS.FORM_FIELD,
			form_name: FORM_NAMES.ADD_EDIT_PET,
			field_name: formField,
			field_value: value,
			field_type: FORM_FIELD_TYPES.RADIO,
			field_id: `${FORM_FIELD_TYPES.RADIO}-${FORM_NAMES.ADD_EDIT_PET}-${formField}`
		});
	}

	private addPet(): void {
		const {
			name,
			species,
			gender,
			breed,
			age: ageInYears,
			serviceDog
		} = this.addEditPetForm.getRawValue();
		/** HACK: */
		if (typeof breed === `string`) {
			this.breed?.patchValue(null);
			this.breed?.updateValueAndValidity();

			return;
		}
		if (ageInYears === `999`) {
			this.showAgeError = true;

			return;
		}
		const analyticsInfo = this.utmService.getUtmCookie();
		let addPetRequest: Pet = {
			name,
			species,
			gender,
			breedId: breed,
			breedName:
				this.breedsQuery.getAll().find(({ id }) => id === breed)?.name ?? ``,
			ageInYears,
			analyticsInfo,
		};

		// only add service dog attribute for dogs, cats will throw a 500 error
		if (species === 'dog') {
			addPetRequest = {
				...addPetRequest,
				isServiceDog: serviceDog === 'yes' ? true : false,

			};
		}

		this.appActions.setLoading(true, `Fetching Coverage Options`, 2000); // eslint-disable-line @typescript-eslint/no-magic-numbers
		this.orchestratorService
			.addNewPet(addPetRequest)
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe(
				({ quoteDetails, sessionType, id, pets }) => {
					this.storeActions.update({
						quoteDetails: {
							...quoteDetails,
							hadVetVisit: this.sessionQuery.getVetVisit(),
						},
						sessionType,
						id,
						pets,
					});
					this.dialogRef.close();
					this.router.navigate([ROUTES.SELECT_A_POLICY]);
					this.appActions.setLastPetActive();
					const loaderTimeout: number = this.appQuery.getLargeLoaderTimeout();
					if (!!loaderTimeout) {
						setTimeout(() => {
							this.appActions.setLoading(false);
						}, loaderTimeout);
					}
				},
				({ error }) => {
					this.appActions.setLoading(false).setLastPetActive();
					if (error.detail.includes('CA_SALES_VALIDATION_MESSAGE')) {
						this.isAgeError = true;
					} else {
						// eslint-disable-next-line @typescript-eslint/no-unused-expressions
						error.detail.includes(`15`)
							? (this.tooManyPets = true)
							: this.router.navigate([ROUTES.ERROR]);
					}
				}
			);

		this.dialogRef
			.afterClosed()
			.pipe(first())
			.subscribe(() => {
				const petIndex = this.sessionQuery.getPetIndex(
					this.appQuery.getActivePetId() ?? ``
				);
				this.gtmService.push({
					event: `Added Pet (Pet ${petIndex})`,
					petIndex,
					queryString: `?addpet=${petIndex}`,
				});
			});
	}


	// eslint-disable-next-line @typescript-eslint/member-ordering
	ngOnDestroy(): void {
		this.ngUnsubscribe$.next(true);
		this.ngUnsubscribe$.unsubscribe();
	}
}
